import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Alert,
  Card,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  useGetProfileQuery,
  useProfileMutation,
} from "../slices/userApiSlice";

const UpdateProfileScreen = () => {
  const navigate = useNavigate();

  const { data: user, isLoading, isError } = useGetProfileQuery();
  const [updateProfile, { isLoading: isUpdating }] = useProfileMutation();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [gstNumber, setGstNumber] = useState("");
  const [shopName, setShopName] = useState("");
  const [role, setRole] = useState("");

  useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email || "");
      setPhone(user.phone || "");
      setAddresses(
        user.addresses?.map((addr) => ({
          address: addr.address || "",
          city: addr.city || "",
          pincode: addr.pincode || "",
          phoneNumber: addr.phoneNumber || "",
          area: addr.area || "",
        })) || []
      );
      setGstNumber(user.gstNumber || "");
      setShopName(user.vendorDetails?.shopName || "");
      setRole(user.role);
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        name,
        email,
        phone,
        addresses,
      };

      if (password) {
        payload.password = password;
      }

      if (role === "vendor") {
        payload.gstNumber = gstNumber;
        payload.vendorDetails = {
          shopName,
        };
      }

      await updateProfile(payload).unwrap();
      alert("Profile updated successfully");
      navigate("/profile", { state: { reload: true } });
    } catch (err) {
      console.error(err);
      alert(err?.data?.message || "Failed to update profile");
    }
  };

  if (isLoading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <Spinner animation="border" />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">Error loading profile.</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Button variant="link" onClick={() => navigate("/")}>
        ← Back to Home
      </Button>

      <h2 className="mb-4">Update Profile</h2>

      <Form onSubmit={handleSubmit}>
        {/* Basic Details */}
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Leave blank to keep unchanged"
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Addresses */}
        <h5 className="mt-4">Addresses</h5>
        {addresses.map((address, index) => (
          <Card key={index} className="mb-3">
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      type="text"
                      value={address.address}
                      onChange={(e) => {
                        const updated = [...addresses];
                        updated[index].address = e.target.value;
                        setAddresses(updated);
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      value={address.city}
                      onChange={(e) => {
                        const updated = [...addresses];
                        updated[index].city = e.target.value;
                        setAddresses(updated);
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Pincode</Form.Label>
                    <Form.Control
                      type="text"
                      value={address.pincode}
                      onChange={(e) => {
                        const updated = [...addresses];
                        updated[index].pincode = e.target.value;
                        setAddresses(updated);
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={address.phoneNumber}
                      onChange={(e) => {
                        const updated = [...addresses];
                        updated[index].phoneNumber = e.target.value;
                        setAddresses(updated);
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        ))}

        {/* Vendor Details */}
        {role === "vendor" && (
          <>
            <h5 className="mt-4">Vendor Details</h5>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>GST Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={gstNumber}
                    onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
                    placeholder="Enter GST Number"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Shop Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={shopName}
                    onChange={(e) => setShopName(e.target.value)}
                    placeholder="Enter Shop Name"
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        )}

        {/* Actions */}
        <Row className="mt-4">
          <Col xs={12} sm={6} className="mb-2">
            <Button
              type="submit"
              variant="primary"
              disabled={isUpdating}
              className="w-100"
            >
              {isUpdating ? "Updating..." : "Update Profile"}
            </Button>
          </Col>

          <Col xs={12} sm={6}>
            <Button
              variant="secondary"
              onClick={() => navigate("/profile")}
              className="w-100"
            >
              Cancel
            </Button>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default UpdateProfileScreen;
